import { useEffect, useState } from "react";
import { X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { amazonDoh, fmt, fmt1, fmtMoney } from "../lib/format";
import { patchProduct, deleteProduct, errMsg } from "../lib/api";
import { SourceBadge, StatusBadge } from "./Badges";

const toNum = (s) => (s === "" || s === null || s === undefined ? null : Number(s));
const NumInput = ({ value, onChange, onCommit, testId, placeholder, className = "" }) => (
  <input
    data-testid={testId}
    type="number"
    min="0"
    inputMode="decimal"
    value={value}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onCommit}
    onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    className={`num h-8 rounded-sm border border-slate-700 bg-[#182232] px-2 text-right text-xs text-slate-100 outline-none transition-colors focus:border-sky-500 ${className}`}
  />
);

const CapacityInputs = ({ p, save }) => {
  const [caps, setCaps] = useState([p.cap_m1 ?? "", p.cap_m2 ?? "", p.cap_m3 ?? ""]);
  useEffect(() => { setCaps([p.cap_m1 ?? "", p.cap_m2 ?? "", p.cap_m3 ?? ""]); }, [p.cap_m1, p.cap_m2, p.cap_m3]);
  return (
    <div className="flex gap-1">
      {[1, 2, 3].map((m) => (
        <NumInput
          key={m}
          testId={`capacity-m${m}-input-${p.code}`}
          value={caps[m - 1]}
          placeholder={`M${m}`}
          onChange={(v) => setCaps(caps.map((c, i) => (i === m - 1 ? v : c)))}
          onCommit={() => toNum(caps[m - 1]) !== (p[`cap_m${m}`] ?? null) && save({ [`cap_m${m}`]: toNum(caps[m - 1]) })}
          className="w-24"
        />
      ))}
    </div>
  );
};

export const StockRow = ({ p, target, onSaved }) => {
  const [override, setOverride] = useState(p.managed_count ?? "");
  const [cost, setCost] = useState(p.unit_cost ?? "");
  const [pallet, setPallet] = useState(p.pallet_qty ?? "");
  const [flash, setFlash] = useState(false);
  useEffect(() => { setOverride(p.managed_count ?? ""); }, [p.managed_count]);
  useEffect(() => { setCost(p.unit_cost ?? ""); }, [p.unit_cost]);
  useEffect(() => { setPallet(p.pallet_qty ?? ""); }, [p.pallet_qty]);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(false), 900);
    return () => clearTimeout(t);
  }, [flash]);

  const ov = toNum(override);
  const unitCost = toNum(cost);
  const palletQty = toNum(pallet);
  const sapUnits = p.sap_unit === "units" ? p.sap_units : (p.sap_free_stock || 0) * (palletQty || 0);
  const effective = ov !== null ? ov : sapUnits;
  const doh = p.site_count ? amazonDoh(effective, p.two_week_max, p.three_week_max) : null;
  const weeks = p.weekly_demand ? effective / p.weekly_demand : null;
  const value = unitCost !== null ? effective * unitCost : null;
  const status = doh === null ? null : doh < target ? "low" : "ok";
  const id = p.code;

  const save = async (body, msg) => {
    try {
      await patchProduct(p.code, body);
      setFlash(true);
      await onSaved();
      if (msg) toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const commitOverride = () => ov !== (p.managed_count ?? null) && save({ managed_count: ov }, ov === null ? `${id}: back to SAP stock` : `${id}: our stock set to ${fmt(ov)}`);
  const commitCost = () => unitCost !== (p.unit_cost ?? null) && save({ unit_cost: unitCost });
  const commitPallet = () => palletQty !== (p.pallet_qty ?? null) && save({ pallet_qty: palletQty });
  const remove = async () => {
    if (!window.confirm(`Remove ${id} from the product list?`)) return;
    try { await deleteProduct(id); await onSaved(); toast.success(`${id} removed`); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <tr data-testid={`stock-row-${id}`} className={flash ? "flash" : ""}>
      <td>
        <div className="num text-xs text-slate-100">{p.item}<span className="text-slate-500">-{p.country}</span></div>
        <div className="text-[11px] text-slate-500">{p.friendly_name || "—"}</div>
      </td>
      <td>
        <div className="num text-xs text-slate-300">{p.sap_code || <span className="text-red-400">no SAP code</span>}</div>
        <div className="max-w-[180px] truncate text-[11px] text-slate-500" title={p.description}>{p.description}</div>
      </td>
      <td><NumInput testId={`pallet-qty-input-${id}`} value={pallet} onChange={setPallet} onCommit={commitPallet} className="w-20" /></td>
      <td className="num text-right text-xs text-slate-400" data-testid={`sap-units-${id}`}>
        {fmt(sapUnits)}
        <div className="text-[10px] text-slate-600">{p.sap_free_stock === null ? "not in SAP file" : `${fmt1(p.sap_free_stock)} ${p.sap_unit === "units" ? "units" : "pal"}`}</div>
      </td>
      <td>
        <div className="flex items-center gap-1">
          <NumInput testId={`stock-override-input-${id}`} value={override} onChange={setOverride} onCommit={commitOverride} placeholder="use SAP" className={`w-32 ${ov !== null ? "border-amber-500/60" : ""}`} />
          <button data-testid={`clear-override-btn-${id}`} disabled={p.managed_count === null || p.managed_count === undefined} onClick={() => { setOverride(""); save({ managed_count: null }, `${id}: back to SAP stock`); }} title="Clear our stock holding" className="rounded-sm p-1 text-slate-500 transition-colors hover:bg-slate-800 hover:text-amber-400 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-500">
            <X className="h-4 w-4" />
          </button>
        </div>
      </td>
      <td><SourceBadge source={ov !== null ? "override" : "sap"} testId={`source-badge-${id}`} /></td>
      <td className="num text-right text-xs font-semibold text-slate-100" data-testid={`effective-stock-${id}`}>{fmt(effective)}</td>
      <td className="num text-right text-xs text-slate-400">{fmt(p.weekly_demand)}</td>
      <td className="num text-right text-xs" data-testid={`doh-${id}`}>{doh === null ? "—" : fmt(Math.floor(doh))}</td>
      <td className="num text-right text-xs" data-testid={`weeks-cover-${id}`}>{fmt1(weeks)}</td>
      <td><StatusBadge status={status} testId={`status-${id}`} /></td>
      <td><NumInput testId={`unit-cost-input-${id}`} value={cost} onChange={setCost} onCommit={commitCost} placeholder="£ / unit" className="w-24" /></td>
      <td className="num text-right text-xs text-sky-300" data-testid={`stock-value-${id}`}>{fmtMoney(value)}</td>
      <td><CapacityInputs p={p} save={save} /></td>
      <td>
        <button data-testid={`delete-product-btn-${id}`} onClick={remove} className="rounded-sm p-1 text-slate-600 transition-colors hover:bg-slate-800 hover:text-red-400" title="Remove product">
          <Trash2 className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};
